import { STRINGS } from '@/constants/strings';

import type { CoachNotification, CoachNotificationApiItem, CoachNotificationType } from './types';

const strings = STRINGS.coach.notifications;

export const COACH_NOTIFICATION_TYPE_LABELS: Record<CoachNotificationType, string> = {
  'class-reminders': strings.types.classReminders,
  'booking-activities': strings.types.bookingActivities,
  system: strings.types.system,
};

export const COACH_NOTIFICATION_TYPE_ICONS = {
  'class-reminders': 'IcNotification',
  'booking-activities': 'IcNotificationClass',
  system: 'IcNotificationSettingsFill',
} as const;

/** Backend `category` → app grouping. Anything absent here resolves to `system`. */
const CATEGORY_TO_TYPE: Record<string, CoachNotificationType> = {
  classReminders: 'class-reminders',
  bookingActivities: 'booking-activities',
  system: 'system',
};

export function resolveCoachNotificationType(category: string): CoachNotificationType {
  return CATEGORY_TO_TYPE[category] ?? 'system';
}

function plural(value: number, unit: string) {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

/** Relative age label per design — "Now", "15 mins", "1 hour", "2 days", "1 month". */
function formatNotificationTime(createdAt: string): string {
  const timestamp = new Date(createdAt).getTime();
  if (Number.isNaN(timestamp)) return '';

  const elapsed = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return strings.time.now;
  if (minutes < 60) return plural(minutes, strings.time.minute);

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, strings.time.hour);

  const days = Math.floor(hours / 24);
  if (days < 30) return plural(days, strings.time.day);

  const months = Math.floor(days / 30);
  if (months < 12) return plural(months, strings.time.month);

  return plural(Math.floor(months / 12), strings.time.year);
}

export function mapCoachNotification(item: CoachNotificationApiItem): CoachNotification {
  const type = resolveCoachNotificationType(item.category);
  const receivedAt = new Date(item.createdAt).getTime();

  return {
    id: item.id,
    type,
    typeLabel: COACH_NOTIFICATION_TYPE_LABELS[type],
    title: item.title,
    subtitle: item.subtitle ?? '',
    // Unparseable dates sort last rather than poisoning the whole list with NaN.
    receivedAt: Number.isNaN(receivedAt) ? 0 : receivedAt,
    timeLabel: formatNotificationTime(item.createdAt),
    isRead: item.read,
    icon: COACH_NOTIFICATION_TYPE_ICONS[type],
    destination: item.destination ?? null,
    playerId: item.playerId ?? null,
    sessionId: item.sessionId ?? null,
    bookingId: item.bookingId ?? null,
    apiType: item.type,
  };
}
